import { CouponFilter, type Filter } from 'mobx/Coupons'
import styled from 'styled-components'
import IconComponent from 'themes/IconComponent'
import TypographyPro from 'themes/TypographyPro'
import { getTranslatedTextByKey } from 'utils/utils'
import { filterToString } from './CouponsFilters'
import WithGuard from 'HOCs/withGuard'
import { Guard } from 'mobx/Infra/Infra.type'
import ButtonBase from 'components/common/ButtonBase'
import { inject, observer } from 'mobx-react'
import type _Account from 'mobx/Account'
import { translateOrderType } from 'mobx/Coupons/utils'

const EmptyStateContainer = styled.div`
	padding-top: 3.75rem;
	display: flex;
	flex-direction: column;
	gap: 1.25rem;
`

const EmptyStateText = styled(TypographyPro).attrs({ variant: 'Subhead' })`
	color: var(--inactive);
	width: fit-content;
`

const Icon = styled(IconComponent).attrs({ asset: '/icons/discount.svg' })`
	height: 2.5rem;
`

const CouponTypeTitle = styled(TypographyPro).attrs({ variant: 'h3' })`
	color: var(--strokeMenuTitle);
`

const MessageContainer = styled.div`
	display: flex;
	align-items: center;
	gap: 0.75rem;
`

const Button = styled(ButtonBase)`
	width: fit-content;
`

interface Props {
	filter: Filter
	onSignIn?: () => void
	Account?: typeof _Account
}

const EmptyState = ({ filter, onSignIn, Account }: Props) => {
	const requireLogin = !Account?.isAuthenticated() && filter === CouponFilter.MY_COUPONS

	const getText = () => {
		if (filter === CouponFilter.ALL) {
			return getTranslatedTextByKey('eCommerce.coupons.empty.all', "Looks like you don't have any coupons in your wallet yet!")
		}

		if (requireLogin) {
			return getTranslatedTextByKey('eCommerce.coupons.empty.myCouponsGuest', 'You need to be logged in to see your personal coupons')
		}

		if (filter === CouponFilter.MY_COUPONS) {
			return getTranslatedTextByKey(`eCommerce.coupons.empty.${filter}`, `No coupons found under "${filterToString[filter]}"`)
		}

		return getTranslatedTextByKey(`eCommerce.coupons.empty.${filter}`, `No ${filterToString[filter]} coupons found`)
	}

	const signupTitle = getTranslatedTextByKey('eCommerce.signIn.couponWalletTitleText', 'Unlock Your Exclusive Coupons!')
	const signupSubTitle = getTranslatedTextByKey(
		'eCommerce.signIn.couponWalletSubTitleText',
		'Log in to access your exclusive coupons and enjoy great deals'
	)

	return (
		<EmptyStateContainer>
			<CouponTypeTitle data-testid={`coupon-title-for-${filter}`}>{translateOrderType(filter).toUpperCase()}</CouponTypeTitle>
			<MessageContainer data-testid="message-container">
				<Icon />
				<EmptyStateText>{getText()}</EmptyStateText>
			</MessageContainer>
			{requireLogin && (
				<WithGuard
					context={Guard.Context.wallet}
					type={Guard.Type.event}
					name={Guard.Name.applyCoupon}
					overrideSignupParams={{ signupTitle, signupSubTitle, signupDisplaySkipSection: false }}
				>
					<Button data-testid="my-coupons-signin-button" onClick={onSignIn}>
						{getTranslatedTextByKey('eCommerce.coupons.empty.signIn', 'Sign In')}
					</Button>
				</WithGuard>
			)}
		</EmptyStateContainer>
	)
}

export default inject('Account')(observer(EmptyState))
